// Shared text normalization for the Smart Asset Import engine.
//
// Normalization is used for TWO things only:
//   1. matching source headers to known aliases (columnMapper)
//   2. exact-matching cell values against AssetPro master data (referenceResolver)
//
// IMPORTANT (data-reliability rule): this is NOT fuzzy matching. We only fold away
// cosmetic differences that are never semantically meaningful — surrounding/inner
// whitespace, letter case, and the literal characters used to separate words. We do
// NOT do edit-distance, token-overlap, stemming, or any "looks similar" logic.
// "KIRLOSKER" and "KIRLOSKAR" normalize to DIFFERENT keys on purpose, so a spelling
// variant is treated as a distinct (and therefore unmatched) value, not silently merged.

import { decodeHtmlEntities } from './htmlDecode';

/**
 * Normalize a string into a stable comparison key.
 * - HTML-decodes (so `JCB &amp; Co` matches `JCB & Co`)
 * - lowercases
 * - trims and collapses internal whitespace
 * - converts runs of separator punctuation (space, _, -, /, \, .) to a single space
 *   so "Reg No", "reg_no" and "reg-no" share a key — but letters/digits are untouched.
 *
 * Returns '' for null/undefined/blank input.
 */
export const normalizeKey = (value) => {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (typeof decodeHtmlEntities === 'function') {
    try {
      s = decodeHtmlEntities(s);
    } catch (e) {
      /* fall back to raw string on any decode error */
    }
  }
  return s
    .toLowerCase()
    .replace(/[\s_\-/\\.]+/g, ' ') // separators -> single space
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Classify a raw cell value for provenance tracking.
 * Distinguishes a genuinely missing value from a literal zero — required so the
 * pipeline never coalesces EMPTY -> 0 (or EMPTY -> 1) silently.
 *
 * @returns {'EMPTY'|'ZERO'|'VALUE'}
 */
export const classifyCell = (value) => {
  if (value === null || value === undefined) return 'EMPTY';
  if (typeof value === 'number') return value === 0 ? 'ZERO' : 'VALUE';
  const s = String(value).trim();
  if (s === '') return 'EMPTY';
  if (/^0+(\.0+)?$/.test(s)) return 'ZERO'; // "0", "00", "0.0"
  return 'VALUE';
};

/** Trim a value to a clean display string without otherwise altering it. */
export const cleanString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim();
};
